import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";

const SpecialBusinessHoursDialog = ({
  open,
  onClose,
  onSave,
  item,
  loading = false,
  lang,
}) => {
  const isFrench = lang === "FR";
  const isEdit = Boolean(item);

  const [date, setDate] = useState("");
  const [opening, setOpening] = useState("");
  const [closing, setClosing] = useState("");
  const [closedAllDay, setClosedAllDay] = useState(false);
  const [error, setError] = useState("");

  // ============================================================
  // HELPERS
  // ============================================================

  const formatDateForInput = (value) => {
    if (!value) {
      return "";
    }

    return String(value).substring(0, 10);
  };

  const getToday = () => {
    const today = new Date();

    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  // ============================================================
  // NORMALIZE TIME
  // ============================================================
  // Support:
  // 00:00
  // 12:00
  // 00:00:00
  // 12:00:00
  // 12:00 AM
  // 12:00 PM
  // ============================================================

  const normalizeTime = (value) => {
    if (!value) {
      return "";
    }

    const time = String(value).trim().toUpperCase();

    // 12-hour format
    if (time === "12:00 AM") {
      return "00:00";
    }

    if (time === "12:00 PM") {
      return "12:00";
    }

    // HH:mm:ss -> HH:mm
    if (/^\d{2}:\d{2}:\d{2}$/.test(time)) {
      return time.substring(0, 5);
    }

    // Already HH:mm
    if (/^\d{2}:\d{2}$/.test(time)) {
      return time;
    }

    return time;
  };

  // ============================================================
  // CHECK FULL DAY CLOSED
  // ============================================================

  const isFullDayClosed = (openingValue, closingValue, stateValue) => {
    const normalizedOpening = normalizeTime(openingValue);

    const normalizedClosing = normalizeTime(closingValue);

    const normalizedState = String(stateValue || "")
      .trim()
      .toLowerCase();

    // Explicit state returned by API
    if (normalizedState === "closed") {
      return true;
    }

    // Full-day closure represented by hours
    return normalizedOpening === "00:00" && normalizedClosing === "12:00";
  };

  // ============================================================
  // INITIALIZE FORM
  // CREATE + EDIT
  // ============================================================

  useEffect(() => {
    if (!open) {
      return;
    }

    setError("");

    // ============================================================
    // EDIT
    // ============================================================

    if (item) {
      const itemOpening = item.opening || "";
      const itemClosing = item.closing || "";

      // Support lowercase / uppercase API properties
      const itemState = item.state ?? item.State;

      // ----------------------------------------------------------
      // DATE
      // ----------------------------------------------------------

      setDate(formatDateForInput(item.date));

      // ----------------------------------------------------------
      // HOURS
      // ----------------------------------------------------------
      // IMPORTANT:
      // We keep the existing values when editing.
      // We DO NOT replace them with 00:00 / 12:00.
      // ----------------------------------------------------------

      setOpening(itemOpening);
      setClosing(itemClosing);

      // ----------------------------------------------------------
      // DETECT CLOSED ALL DAY
      // ----------------------------------------------------------

      const normalizedOpening = normalizeTime(itemOpening);

      const normalizedClosing = normalizeTime(itemClosing);

      const isClosed = isFullDayClosed(itemOpening, itemClosing, itemState);

      console.log("========================================");

      console.log("EDIT SPECIAL BUSINESS HOURS");

      console.log("========================================");

      console.log("Item:", item);

      console.log("Opening original:", itemOpening);

      console.log("Closing original:", itemClosing);

      console.log("Opening normalized:", normalizedOpening);

      console.log("Closing normalized:", normalizedClosing);

      console.log("State:", itemState);

      console.log("Closed all day:", isClosed);

      console.log("========================================");

      setClosedAllDay(isClosed);
    }

    // ============================================================
    // CREATE
    // ============================================================
    else {
      setDate("");
      setOpening("");
      setClosing("");
      setClosedAllDay(false);
    }
  }, [open, item]);

  // ============================================================
  // CLOSED ALL DAY CHECKBOX
  // ============================================================

  const handleClosedAllDayChange = (event) => {
    const checked = event.target.checked;

    setClosedAllDay(checked);
    setError("");

    // ==========================================================
    // CHECKED
    // ==========================================================

    if (checked) {
      // Full-day closure
      setOpening("00:00");
      setClosing("12:00");

      return;
    }

    // ==========================================================
    // UNCHECKED
    // ==========================================================
    // User explicitly wants both fields emptied.
    // ==========================================================

    setOpening("");
    setClosing("");
  };

  // ============================================================
  // OPENING CHANGE
  // ============================================================

  const handleOpeningChange = (value) => {
    setOpening(value);
    setError("");

    const normalizedOpening = normalizeTime(value);

    const normalizedClosing = normalizeTime(closing);

    // Automatically check when:
    // Opening = 00:00
    // Closing = 12:00

    const isClosed =
      normalizedOpening === "00:00" && normalizedClosing === "12:00";

    setClosedAllDay(isClosed);
  };

  // ============================================================
  // CLOSING CHANGE
  // ============================================================

  const handleClosingChange = (value) => {
    setClosing(value);
    setError("");

    const normalizedOpening = normalizeTime(opening);

    const normalizedClosing = normalizeTime(value);

    // Automatically check when:
    // Opening = 00:00
    // Closing = 12:00

    const isClosed =
      normalizedOpening === "00:00" && normalizedClosing === "12:00";

    setClosedAllDay(isClosed);
  };

  // ============================================================
  // VALIDATION
  // ============================================================

  const validate = () => {
    // ==========================================================
    // DATE REQUIRED
    // ==========================================================

    if (!date) {
      setError(
        isFrench ? "Veuillez sélectionner une date." : "Please select a date.",
      );

      return false;
    }

    // ==========================================================
    // CURRENT / FUTURE DATE ONLY
    // ==========================================================

    if (date < getToday()) {
      setError(
        isFrench
          ? "La date doit être aujourd'hui ou une date future."
          : "The date must be today or a date in the future.",
      );

      return false;
    }

    // ==========================================================
    // CLOSED ALL DAY
    // ==========================================================

    if (closedAllDay) {
      return true;
    }

    // ==========================================================
    // OPENING REQUIRED
    // ==========================================================

    if (!opening) {
      setError(
        isFrench
          ? "Veuillez sélectionner une heure d'ouverture."
          : "Please select an opening time.",
      );

      return false;
    }

    // ==========================================================
    // CLOSING REQUIRED
    // ==========================================================

    if (!closing) {
      setError(
        isFrench
          ? "Veuillez sélectionner une heure de fermeture."
          : "Please select a closing time.",
      );

      return false;
    }

    // ==========================================================
    // OPENING BEFORE CLOSING
    // ==========================================================

    if (opening >= closing) {
      setError(
        isFrench
          ? "L'heure d'ouverture doit être avant l'heure de fermeture."
          : "The opening time must be before the closing time.",
      );

      return false;
    }

    return true;
  };

  // ============================================================
  // SAVE
  // CREATE + EDIT
  // ============================================================

  const handleSubmit = async () => {
    setError("");

    // Validate
    if (!validate()) {
      return;
    }

    // ==========================================================
    // PAYLOAD
    // ==========================================================

    const payload = {
      date,

      opening: closedAllDay ? "00:00" : opening,

      closing: closedAllDay ? "12:00" : closing,

      state: closedAllDay ? "Closed" : "Open",
    };

    console.log(
      `Special Business Hours ${isEdit ? "Update" : "Create"} Payload:`,
      payload,
    );

    // ==========================================================
    // SAVE
    // ==========================================================

    try {
      await onSave(payload);
    } catch (err) {
      console.error("Error saving special business hours:", err);

      setError(
        isFrench
          ? err?.messageFr ||
              "Une erreur est survenue lors de l'enregistrement."
          : err?.messageEn ||
              "An error occurred while saving the special business hours.",
      );
    }
  };

  // ============================================================
  // CLOSE
  // ============================================================

  const handleClose = () => {
    if (loading) {
      return;
    }

    setError("");

    onClose();
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* ====================================================== */}
      {/* HEADER */}
      {/* ====================================================== */}

      <DialogTitle
        sx={{
          px: 3,
          py: 2.5,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                color: "#fff",
              }}
            >
              {isEdit ? (
                <AccessTimeRoundedIcon />
              ) : (
                <EventAvailableRoundedIcon />
              )}
            </Box>

            <Box>
              <Typography variant="h6" fontWeight={700}>
                {isEdit
                  ? isFrench
                    ? "Modifier les heures spéciales"
                    : "Edit Special Business Hours"
                  : isFrench
                    ? "Ajouter des heures spéciales"
                    : "Add Special Business Hours"}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.25,
                }}
              >
                {isEdit
                  ? isFrench
                    ? "Modifiez l'horaire exceptionnel de cette date."
                    : "Update the exceptional hours for this date."
                  : isFrench
                    ? "Configurez une ouverture exceptionnelle pour une date."
                    : "Configure exceptional opening hours for a date."}
              </Typography>
            </Box>
          </Stack>

          <IconButton
            onClick={handleClose}
            disabled={loading}
            aria-label={isFrench ? "Fermer" : "Close"}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider />

      {/* ====================================================== */}
      {/* CONTENT */}
      {/* ====================================================== */}

      <DialogContent
        sx={{
          px: 3,
          py: 3,
        }}
      >
        <Stack spacing={3}>
          {/* ================================================== */}
          {/* ERROR */}
          {/* ================================================== */}

          {error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                borderRadius: 2,
              }}
            >
              {error}
            </Alert>
          )}

          {/* ================================================== */}
          {/* DATE */}
          {/* ================================================== */}

          <Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                mb: 1,
              }}
            >
              <CalendarMonthRoundedIcon fontSize="small" color="primary" />

              <Typography fontWeight={600}>
                {isFrench ? "Date" : "Date"}
              </Typography>
            </Stack>

            <TextField
              fullWidth
              type="date"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);

                setError("");
              }}
              disabled={loading}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: getToday(),
              }}
              helperText={
                isFrench
                  ? "Seules les dates courantes et futures sont autorisées."
                  : "Only current and future dates are allowed."
              }
            />
          </Box>

          {/* ================================================== */}
          {/* CLOSED ALL DAY */}
          {/* ================================================== */}

          <Box
            sx={{
              border: "1px solid",

              borderColor: closedAllDay ? "error.main" : "divider",

              borderRadius: 2,

              px: 2,

              py: 1,

              backgroundColor: closedAllDay ? "error.50" : "background.paper",

              transition: "all 0.2s ease",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={closedAllDay}
                  onChange={handleClosedAllDayChange}
                  disabled={loading}
                  color="error"
                />
              }
              label={
                <Box>
                  <Typography
                    fontWeight={700}
                    color={closedAllDay ? "error.main" : "text.primary"}
                  >
                    {isFrench ? "Fermé toute la journée" : "Closed all day"}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {isFrench
                      ? "Le commerce sera fermé pendant toute cette journée."
                      : "The location will be closed for the entire day."}
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* ================================================== */}
          {/* OPENING / CLOSING */}
          {/* ================================================== */}

          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={2}
          >
            {/* ============================================== */}
            {/* OPENING */}
            {/* ============================================== */}

            <Box sx={{ flex: 1 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  mb: 1,
                }}
              >
                <AccessTimeRoundedIcon
                  fontSize="small"
                  color={closedAllDay ? "disabled" : "success"}
                />

                <Typography
                  fontWeight={600}
                  color={closedAllDay ? "text.disabled" : "text.primary"}
                >
                  {isFrench ? "Ouverture" : "Opening"}
                </Typography>
              </Stack>

              <TextField
                fullWidth
                type="time"
                value={opening}
                onChange={(e) => handleOpeningChange(e.target.value)}
                disabled={loading || closedAllDay}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  step: 60,
                }}
              />
            </Box>

            {/* ============================================== */}
            {/* CLOSING */}
            {/* ============================================== */}

            <Box sx={{ flex: 1 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  mb: 1,
                }}
              >
                <AccessTimeRoundedIcon
                  fontSize="small"
                  color={closedAllDay ? "disabled" : "error"}
                />

                <Typography
                  fontWeight={600}
                  color={closedAllDay ? "text.disabled" : "text.primary"}
                >
                  {isFrench ? "Fermeture" : "Closing"}
                </Typography>
              </Stack>

              <TextField
                fullWidth
                type="time"
                value={closing}
                onChange={(e) => handleClosingChange(e.target.value)}
                disabled={loading || closedAllDay}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  step: 60,
                }}
              />
            </Box>
          </Stack>

          {/* ================================================== */}
          {/* INFORMATION */}
          {/* ================================================== */}

          <Alert
            severity={closedAllDay ? "warning" : "info"}
            icon={<EventAvailableRoundedIcon />}
            sx={{
              borderRadius: 2,
            }}
          >
            {closedAllDay
              ? isFrench
                ? "Cette journée sera complètement fermée. Les heures seront enregistrées de 00:00 à 12:00."
                : "This day will be completely closed. The hours will be saved as 12:00 AM to 12:00 PM."
              : isFrench
                ? "L'heure d'ouverture doit être antérieure à l'heure de fermeture."
                : "The opening time must be earlier than the closing time."}
          </Alert>
        </Stack>
      </DialogContent>

      <Divider />

      {/* ====================================================== */}
      {/* ACTIONS */}
      {/* ====================================================== */}

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {isFrench ? "Annuler" : "Cancel"}
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            minWidth: 120,
          }}
        >
          {loading
            ? isFrench
              ? "Enregistrement..."
              : "Saving..."
            : isEdit
              ? isFrench
                ? "Enregistrer"
                : "Save Changes"
              : isFrench
                ? "Ajouter"
                : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SpecialBusinessHoursDialog;
